import { useState, useContext, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  alpha,
  Checkbox,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Chip,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { ColorModeContext } from '../../../contexts/ColorModeContext';
import { palette, typography, theme } from '../../../theme/colors';
import { useBulkUpdateVariants, useVariants } from '../../../hooks/useVariants';
import { useSnackbar } from '../../../components/SnackbarProvider';
import type { BulkUpdateVariantsDto } from '../../../types';

interface VariantBulkEditorProps {
  open: boolean;
  onClose: () => void;
  productId: string;
}

interface BulkEditFormData {
  priceAdjustment?: number;
  isActive?: boolean;
  applyPriceAdjustment: boolean;
  applyIsActive: boolean;
}

export const VariantBulkEditor = ({
  open,
  onClose,
  productId,
}: VariantBulkEditorProps) => {
  const { showMessage } = useSnackbar();
  const { mode } = useContext(ColorModeContext);
  const [selectedVariantIds, setSelectedVariantIds] = useState<Set<string>>(new Set());

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<BulkEditFormData>({
    defaultValues: {
      priceAdjustment: 0,
      isActive: true,
      applyPriceAdjustment: false,
      applyIsActive: false,
    },
  });

  const applyPriceAdjustment = watch('applyPriceAdjustment');
  const applyIsActive = watch('applyIsActive');

  const colors = mode === 'dark' ? {
    bg: palette.background,
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    borderLight: palette.borderLight,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.background,
    surface: palette.light.surface,
    surfaceElevated: palette.light.surfaceElevated,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  // Fetch variants
  const { data: variants = [], isLoading } = useVariants(productId);

  const bulkUpdateMutation = useBulkUpdateVariants(productId);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      reset();
      setSelectedVariantIds(new Set());
    }
  }, [open, reset]);

  const handleToggleVariant = (variantId: string) => {
    const newSelected = new Set(selectedVariantIds);
    if (newSelected.has(variantId)) {
      newSelected.delete(variantId);
    } else {
      newSelected.add(variantId);
    }
    setSelectedVariantIds(newSelected);
  };

  const handleToggleAll = () => {
    if (selectedVariantIds.size === variants.length) {
      setSelectedVariantIds(new Set());
    } else {
      setSelectedVariantIds(new Set(variants.map(v => v.id)));
    }
  };

  const formatAttributes = (attributes: Record<string, unknown>): string => {
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  const onSubmit = async (data: BulkEditFormData) => {
    if (selectedVariantIds.size === 0) {
      showMessage('Please select at least one variant', 'warning');
      return;
    }

    if (!data.applyPriceAdjustment && !data.applyIsActive) {
      showMessage('Please select at least one field to update', 'warning');
      return;
    }

    try {
      const updates: BulkUpdateVariantsDto['updates'] = Array.from(selectedVariantIds).map(id => {
        const update: any = { id };

        if (data.applyPriceAdjustment) {
          update.priceAdjustment = data.priceAdjustment || 0;
        }

        if (data.applyIsActive) {
          update.isActive = data.isActive;
        }

        return update;
      });

      await bulkUpdateMutation.mutateAsync({ updates });

      showMessage(
        `Successfully updated ${selectedVariantIds.size} variant${selectedVariantIds.size !== 1 ? 's' : ''}`,
        'success'
      );
      onClose();
    } catch (error: any) {
      showMessage(
        error.response?.data?.message || 'Failed to update variants',
        'error'
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: theme.radius.lg,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: typography.fontFamily.display,
          fontWeight: typography.fontWeight.bold,
          fontSize: typography.fontSize.xl,
          color: colors.textPrimary,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <EditIcon sx={{ color: palette.primary, fontSize: 28 }} />
        Bulk Edit Variants
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Info Banner */}
        <Alert
          severity="info"
          sx={{
            mb: 3,
            borderRadius: theme.radius.sm,
            fontFamily: typography.fontFamily.body,
            backgroundColor: alpha(palette.primary, 0.1),
            border: `1px solid ${alpha(palette.primary, 0.3)}`,
          }}
        >
          Select variants below and choose which fields to update. Changes will be applied to all selected variants.
        </Alert>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: palette.primary }} />
          </Box>
        )}

        {/* Variant Selection */}
        {!isLoading && variants.length === 0 && (
          <Alert
            severity="warning"
            sx={{
              borderRadius: theme.radius.sm,
              fontFamily: typography.fontFamily.body,
            }}
          >
            No variants found. Create variants first before bulk editing.
          </Alert>
        )}

        {!isLoading && variants.length > 0 && (
          <>
            {/* Select All Button */}
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedVariantIds.size === variants.length && variants.length > 0}
                    indeterminate={selectedVariantIds.size > 0 && selectedVariantIds.size < variants.length}
                    onChange={handleToggleAll}
                    sx={{
                      color: palette.primary,
                      '&.Mui-checked': {
                        color: palette.primary,
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: typography.fontFamily.body,
                      fontWeight: typography.fontWeight.medium,
                      color: colors.textPrimary,
                    }}
                  >
                    Select All
                  </Typography>
                }
              />
              <Chip
                label={`${selectedVariantIds.size} of ${variants.length} selected`}
                sx={{
                  fontFamily: typography.fontFamily.mono,
                  fontWeight: typography.fontWeight.bold,
                  backgroundColor: alpha(palette.primary, 0.1),
                  color: palette.primary,
                  border: `1px solid ${palette.primary}`,
                }}
              />
            </Box>

            {/* Variant List */}
            <TableContainer
              sx={{
                mb: 3,
                maxHeight: 300,
                border: `1px solid ${colors.border}`,
                borderRadius: theme.radius.sm,
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: colors.surfaceElevated }}>
                    <TableCell
                      sx={{
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${colors.border}`,
                        width: 50,
                      }}
                    />
                    <TableCell
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        fontSize: typography.fontSize.xs,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                        color: colors.textMuted,
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${colors.border}`,
                      }}
                    >
                      SKU
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        fontSize: typography.fontSize.xs,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                        color: colors.textMuted,
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${colors.border}`,
                      }}
                    >
                      Attributes
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: typography.fontFamily.mono,
                        fontWeight: typography.fontWeight.bold,
                        fontSize: typography.fontSize.xs,
                        textTransform: 'uppercase',
                        letterSpacing: typography.letterSpacing.widest,
                        color: colors.textMuted,
                        backgroundColor: colors.surfaceElevated,
                        borderBottom: `2px solid ${colors.border}`,
                      }}
                    >
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {variants.map((variant) => (
                    <TableRow
                      key={variant.id}
                      hover
                      onClick={() => handleToggleVariant(variant.id)}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: selectedVariantIds.has(variant.id)
                          ? alpha(palette.primary, 0.1)
                          : 'transparent',
                        '&:hover': {
                          backgroundColor: selectedVariantIds.has(variant.id)
                            ? alpha(palette.primary, 0.15)
                            : alpha(colors.surfaceElevated, 0.5),
                        },
                      }}
                    >
                      <TableCell sx={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                        <Checkbox
                          checked={selectedVariantIds.has(variant.id)}
                          onChange={() => handleToggleVariant(variant.id)}
                          sx={{
                            color: palette.primary,
                            '&.Mui-checked': {
                              color: palette.primary,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.mono,
                          fontSize: typography.fontSize.sm,
                          color: colors.textPrimary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {variant.sku}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: typography.fontFamily.body,
                          fontSize: typography.fontSize.sm,
                          color: colors.textSecondary,
                          borderBottom: `1px solid ${colors.borderLight}`,
                        }}
                      >
                        {formatAttributes(variant.attributes)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                        <Chip
                          label={variant.isActive ? 'Active' : 'Inactive'}
                          color={variant.isActive ? 'success' : 'default'}
                          size="small"
                          sx={{
                            fontFamily: typography.fontFamily.mono,
                            fontWeight: typography.fontWeight.medium,
                            fontSize: typography.fontSize.xs,
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider sx={{ mb: 3, borderColor: colors.border }} />

            {/* Edit Fields */}
            <Typography
              variant="h6"
              sx={{
                fontFamily: typography.fontFamily.display,
                fontWeight: typography.fontWeight.bold,
                color: colors.textPrimary,
                mb: 2,
                fontSize: typography.fontSize.md,
              }}
            >
              Fields to Update
            </Typography>

            {/* Price Adjustment */}
            <Box sx={{ mb: 3 }}>
              <FormControlLabel
                control={
                  <Controller
                    name="applyPriceAdjustment"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: palette.primary,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: palette.primary,
                          },
                        }}
                      />
                    )}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: typography.fontFamily.body,
                      fontWeight: typography.fontWeight.medium,
                      color: colors.textPrimary,
                    }}
                  >
                    Update Price Adjustment
                  </Typography>
                }
              />
              {applyPriceAdjustment && (
                <Controller
                  name="priceAdjustment"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (applyPriceAdjustment && value === undefined) {
                        return 'Price adjustment is required';
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      type="number"
                      onChange={(e) =>
                        field.onChange(
                          e.target.value === '' ? undefined : Number(e.target.value)
                        )
                      }
                      label="Price Adjustment"
                      fullWidth
                      error={!!errors.priceAdjustment}
                      helperText={errors.priceAdjustment?.message}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                        style: {
                          fontFamily: typography.fontFamily.mono,
                        },
                      }}
                      sx={{
                        mt: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: colors.surface,
                          '& fieldset': {
                            borderColor: colors.border,
                          },
                          '&:hover fieldset': {
                            borderColor: palette.primary,
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: palette.primary,
                          },
                        },
                      }}
                    />
                  )}
                />
              )}
            </Box>

            {/* Is Active */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Controller
                    name="applyIsActive"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        {...field}
                        checked={field.value}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: palette.primary,
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: palette.primary,
                          },
                        }}
                      />
                    )}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontFamily: typography.fontFamily.body,
                      fontWeight: typography.fontWeight.medium,
                      color: colors.textPrimary,
                    }}
                  >
                    Update Active Status
                  </Typography>
                }
              />
              {applyIsActive && (
                <FormControlLabel
                  control={
                    <Controller
                      name="isActive"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value}
                          sx={{
                            ml: 2,
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: palette.success,
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: palette.success,
                            },
                          }}
                        />
                      )}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        fontFamily: typography.fontFamily.body,
                        color: colors.textSecondary,
                      }}
                    >
                      Set as Active
                    </Typography>
                  }
                  sx={{ mt: 1, ml: 4 }}
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${colors.border}`,
          p: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={bulkUpdateMutation.isPending}
          startIcon={<CancelIcon />}
          sx={{
            fontFamily: typography.fontFamily.body,
            fontWeight: typography.fontWeight.medium,
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: alpha(colors.border, 0.1),
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={bulkUpdateMutation.isPending || isLoading || variants.length === 0}
          startIcon={
            bulkUpdateMutation.isPending ? (
              <CircularProgress size={16} sx={{ color: palette.light.textPrimary }} />
            ) : (
              <SaveIcon />
            )
          }
          variant="contained"
          sx={{
            fontFamily: typography.fontFamily.body,
            fontWeight: typography.fontWeight.bold,
            backgroundColor: palette.primary,
            color: palette.light.textPrimary,
            '&:hover': {
              backgroundColor: alpha(palette.primary, 0.8),
            },
            '&:disabled': {
              backgroundColor: alpha(palette.primary, 0.3),
              color: alpha(palette.light.textPrimary, 0.5),
            },
          }}
        >
          {bulkUpdateMutation.isPending ? 'Updating...' : 'Apply Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
